/**
 * 聊天页的可视高度（S11 PC-00：聊天占满视口、键盘不遮挡）。
 *
 * 平时整页按 100dvh 锁高（CSS 退路，不靠这里）；只有“输入框聚焦、可视区又明显变矮”才算键盘弹起，这时按可视视口重新量高：
 * - visual：布局视口不变、只有可视视口变矮（安卓 Chrome 默认、iOS Safari）。底栏钉在布局视口底下、被键盘挡住；
 *   页面高度 = 可视视口高，浏览器把可视区往下平移（pageTop）时整页跟着平移，输入区贴着键盘上沿。
 * - resize：布局视口自己变矮（resizes-content、部分内嵌 WebView）。底栏跟着键盘上来，照旧给它留位置。
 * 双指缩放（scale ≠ 1）也会让可视视口变矮，但不算键盘（不能靠禁止缩放来掩盖）；硬件键盘、浮动键盘不挤占可视区，也不算。
 * 浏览器没有 visualViewport 时一律按“没有键盘”：页面照旧是 100dvh 的布局，输入区在底栏上沿，照样能用。
 * 布局视口高用 documentElement.clientHeight（iOS 上 innerHeight 会跟着缩放、键盘变）。
 * 手机真机上的软键盘行为无头浏览器量不到，必须真机实测（见 PC-00 报告里“只有真机能验”的几项）。
 */
import { useLayoutEffect, useState, type CSSProperties, type RefObject } from "react";

export type ChatKeyboard = "none" | "visual" | "resize";

/** 一次取样：布局视口高、同宽下见过的最高布局视口、可视视口、焦点是否在可编辑的框里 */
export interface ViewportSample {
  layoutHeight: number;
  baselineHeight: number;
  vv: { height: number; pageTop: number; scale: number } | null;
  editableFocused: boolean;
}

export interface ChatViewport {
  keyboard: ChatKeyboard;
  /** 当前真正看得见的高度（CSS px）：键盘弹起时是可视视口高，平时是布局视口高 */
  visibleHeight: number;
  /** 看得见的区域在文档里的上沿（CSS px）；平时是 0 */
  visibleTop: number;
}

/** 可视区至少矮这么多才当成软键盘：iOS 外接键盘时的快捷栏（约 44–55px）不算 */
export const KEYBOARD_MIN_PX = 120;

export function readChatViewport(sample: ViewportSample): ChatViewport {
  const calm: ChatViewport = { keyboard: "none", visibleHeight: sample.layoutHeight, visibleTop: 0 };
  const vv = sample.vv;
  if (!vv || !sample.editableFocused) return calm;
  // 双指缩放：可视视口按缩放倍数变小，不是键盘。
  if (Math.abs(vv.scale - 1) > 0.01) return calm;
  if (sample.layoutHeight - vv.height >= KEYBOARD_MIN_PX) return { keyboard: "visual", visibleHeight: vv.height, visibleTop: vv.pageTop };
  if (sample.baselineHeight - sample.layoutHeight >= KEYBOARD_MIN_PX) return { keyboard: "resize", visibleHeight: vv.height, visibleTop: vv.pageTop };
  return calm;
}

export type ChatFold = "none" | "extras" | "all";
/** 看得见的高度低于这个（横屏、竖屏弹键盘）：公告细条、分区切换挪进消息区最上面，跟着消息滚走 */
export const FOLD_EXTRAS_BELOW = 520;
/** 再矮（横屏又弹键盘）：顶栏也挪进去，消息区和输入区先保住 */
export const FOLD_ALL_BELOW = 300;
export function foldFor(visibleHeight: number): ChatFold {
  if (visibleHeight <= 0) return "none";
  return visibleHeight < FOLD_ALL_BELOW ? "all" : visibleHeight < FOLD_EXTRAS_BELOW ? "extras" : "none";
}

/** 同一宽度下见过的最高布局视口（竖横屏各记各的）：resize 型键盘让布局视口变矮，要和它比 */
const baselineByWidth = new Map<number, number>();

const NOT_TYPING = new Set(["button", "checkbox", "radio", "submit", "reset", "file", "range", "color", "image", "hidden"]);
function isEditable(el: Element | null): boolean {
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  if (el instanceof HTMLInputElement) return !el.readOnly && !el.disabled && !NOT_TYPING.has(el.type);
  return el instanceof HTMLElement && el.isContentEditable;
}

function sampleNow(): ViewportSample {
  const layoutHeight = document.documentElement.clientHeight || window.innerHeight;
  const width = document.documentElement.clientWidth || window.innerWidth;
  const baselineHeight = Math.max(baselineByWidth.get(width) ?? 0, layoutHeight);
  baselineByWidth.set(width, baselineHeight);
  const v = window.visualViewport;
  return {
    layoutHeight,
    baselineHeight,
    vv: v ? { height: v.height, pageTop: v.pageTop, scale: v.scale } : null,
    editableFocused: isEditable(document.activeElement),
  };
}

export interface ChatViewportState extends ChatViewport {
  /** 挂在聊天页 main 上的样式变量（见 communicator.css 的 .ps-comm-page--chat） */
  style: CSSProperties;
}

const IDLE: ChatViewportState = { keyboard: "none", visibleHeight: 0, visibleTop: 0, style: {} };
const same = (a: ChatViewportState, b: ChatViewportState) =>
  a.keyboard === b.keyboard && a.visibleHeight === b.visibleHeight && a.visibleTop === b.visibleTop && JSON.stringify(a.style) === JSON.stringify(b.style);

/**
 * 量聊天页该有多高。active = false（“TA 的朋友”那一栏是普通长页面）时不挂任何监听。
 * 页面上沿之上可能还有开发 / 演示提示条（RootLayout 的 ModeRibbon），它的高度从页面自己的 offsetTop 量，不写死。
 */
export function useChatViewport(active: boolean, pageRef: RefObject<HTMLElement | null>): ChatViewportState {
  const [state, setState] = useState<ChatViewportState>(IDLE);
  useLayoutEffect(() => {
    if (!active) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const page = pageRef.current;
      if (!page) return;
      const view = readChatViewport(sampleNow());
      const top = page.offsetTop;
      let vars: Record<string, string>;
      if (view.keyboard === "none") {
        vars = { "--chat-top": `${top}px` };
      } else {
        // 可视区上沿还在提示条之上时，页面不动、只扣掉看得见的那截提示条；可视区往下平移过了提示条，页面跟着平移。
        const pageTop = Math.max(top, view.visibleTop);
        vars = {
          "--chat-top": `${top}px`,
          "--chat-h": `${Math.max(0, Math.round(view.visibleTop + view.visibleHeight - pageTop))}px`,
          "--chat-shift": `${Math.round(pageTop - top)}px`,
        };
      }
      const next: ChatViewportState = { ...view, visibleHeight: Math.round(view.visibleHeight), visibleTop: Math.round(view.visibleTop), style: vars as CSSProperties };
      setState((prev) => (same(prev, next) ? prev : next));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    measure();
    const vv = window.visualViewport;
    vv?.addEventListener("resize", schedule);
    vv?.addEventListener("scroll", schedule);
    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);
    // 失焦时新的焦点还没落定：等下一帧再量
    document.addEventListener("focusin", schedule);
    document.addEventListener("focusout", schedule);
    // 页面上面的提示条（演示提示条可以展开）变高变矮时，页面的上沿跟着变
    const above = pageRef.current?.previousElementSibling ?? null;
    const ro = above && typeof ResizeObserver === "function" ? new ResizeObserver(schedule) : null;
    if (above) ro?.observe(above);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      vv?.removeEventListener("resize", schedule);
      vv?.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      document.removeEventListener("focusin", schedule);
      document.removeEventListener("focusout", schedule);
      ro?.disconnect();
    };
  }, [active, pageRef]);
  return active ? state : IDLE;
}
